// The room's status rail and its readiness gate (lane B7 S14; CR-024 the room's chain, CR-070 required attendees and
// scorecards). The rail draws D11's chain, "Not Ready -> Scheduled -> Roll Call -> Reveal -> Discussion -> Alignment ->
// Outcome Recorded/Blocked", with Blocked as the terminal pill of a branch, never a step. The gate is *Debrief
// readiness*: Ready or Not Ready with every missing control named ("As Coordinator, I see Ready or Not Ready with
// reasons."), read from the scheduling lane's readiness tool; the Coordinator sees exactly this and nothing else.

import { schedulingDebriefReadiness, type ReadinessControlOut, type SchedulingDebriefReadinessOutput } from "@/api/tools";
import {
  DEBRIEF_READINESS_STATUS, DEBRIEF_STATUS, Gate, LOOP_LABELS, METRIC_LABELS, Rail, ROLE_LABELS, ROOTS_LABELS,
  useHs, type GateRow, type RailStep, type StatusWord,
} from "@/mc";
import { hisRoundLabel, isLoopRound } from "../score/round";
import { SUMMARY_LABELS } from "../summary/labels";
import { ROOM_RULES } from "./labels";
import { ROOM_CHAIN, type RoomStatus, type RoomStep } from "./types";

/** One missing control of the scheduling lane's readiness read (B5 `scheduling.debrief_readiness`). */
export type ReadinessControl = ReadinessControlOut;
export type ReadinessOut = SchedulingDebriefReadinessOutput;

export const debriefReadiness = (input: { requisition_id: string; cycle_id: string }) => schedulingDebriefReadiness(input);

const STEP_WORD: Readonly<Record<RoomStep, StatusWord>> = {
  not_ready: DEBRIEF_STATUS.notReady,
  scheduled: DEBRIEF_STATUS.scheduled,
  roll_call: DEBRIEF_STATUS.rollCall,
  reveal: DEBRIEF_STATUS.reveal,
  discussion: DEBRIEF_STATUS.discussion,
  alignment: DEBRIEF_STATUS.alignment,
  outcome_recorded: DEBRIEF_STATUS.outcomeRecorded,
};

export const ROLE_WORD: Readonly<Record<string, string>> = {
  recruiter: ROLE_LABELS.recruiter,
  hm: ROLE_LABELS.hmAbbr,
  hrbp: ROLE_LABELS.hrbpAbbr,
  interviewer: ROLE_LABELS.interviewer,
  coordinator: ROLE_LABELS.coordinator,
};

const ROOTS_WORD: Readonly<Record<string, string>> = {
  respect: ROOTS_LABELS.respect, ownership: ROOTS_LABELS.ownership, opportunity: ROOTS_LABELS.opportunity,
  together: ROOTS_LABELS.together, service: ROOTS_LABELS.service,
};

/** The word of a room status (Blocked included). */
export function roomStatusWord(status: RoomStatus): StatusWord {
  return status === "blocked" ? DEBRIEF_STATUS.blocked : STEP_WORD[status];
}

/** D11 as rail steps: before the current one done, the current one on, the rest next; Blocked ends the chain. */
export function railSteps(status: RoomStatus): RailStep[] {
  const at = status === "blocked" ? ROOM_CHAIN.length - 1 : ROOM_CHAIN.indexOf(status);
  return ROOM_CHAIN.map((step, index) => ({
    label: STEP_WORD[step],
    state: index < at ? "done" : index === at ? (status === "blocked" ? "bad" : "on") : "next",
  }));
}

/** A missing control in his words; its round, author, value or seat as data. */
export function controlText(c: ReadinessControl): string {
  switch (c.code) {
    case "rounds_held":
      return isLoopRound(c.round_label) ? hisRoundLabel(c.round_label ?? "") : [LOOP_LABELS.finalInterviewLoop, c.round_label].filter(Boolean).join(" · ");
    case "scorecards_submitted":
      return [SUMMARY_LABELS.scorecards, c.author_name ?? hisRoundLabel(c.round_label) ?? ROLE_WORD[c.role ?? ""]].filter(Boolean).join(" · ");
    case "roots_coverage":
      return [SUMMARY_LABELS.coverage, ROOTS_WORD[c.value ?? ""] ?? c.value ?? ROOTS_LABELS.roots].join(" · ");
    case "summary_released":
    case "summary_stale":
      return SUMMARY_LABELS.candidateSummary;
    case "roster_complete":
      return [ROOM_RULES.requiredAttendees, ROLE_WORD[c.role ?? ""]].filter(Boolean).join(" · ");
    default:
      return c.gap ?? c.code;
  }
}

export function readinessRows(found: ReadinessOut, extra: readonly string[] = []): GateRow[] {
  const open: GateRow[] = found.controls.map((c, index) => ({
    status: "bad", text: controlText(c), href: c.route ?? undefined, hs: `room.readiness.${index}`,
  }));
  const blocked: GateRow[] = extra.map((text) => ({ status: "bad", text }));
  return [...open, ...blocked];
}

export interface ReadinessProps {
  status: RoomStatus;
  readiness?: ReadinessOut | null | undefined;
  loading?: boolean | undefined;
  /** S07's blocked reasons, in the server's words. */
  blockedReasons?: readonly string[] | undefined;
  /** The readiness read was refused: the rail stands alone. */
  refused?: boolean | undefined;
}

export function Readiness({ status, readiness, loading, blockedReasons = [], refused }: ReadinessProps) {
  const hs = useHs("room.status");
  return (
    <div className="mc-stack" {...hs}>
      <Rail
        label={METRIC_LABELS.debriefReadiness}
        steps={railSteps(status)}
        terminal={status === "blocked" ? { word: DEBRIEF_STATUS.blocked, tone: "red" } : undefined}
        wrap
      />
      {refused ? null : (
        <Gate
          title={METRIC_LABELS.debriefReadiness}
          verdict={readiness ? (readiness.ready ? DEBRIEF_READINESS_STATUS.ready : DEBRIEF_READINESS_STATUS.notReady) : undefined}
          ready={readiness?.ready}
          rows={readiness ? readinessRows(readiness, blockedReasons) : []}
          loading={!!loading && !readiness}
          mode="view"
        />
      )}
    </div>
  );
}
